import ListFilters from "@/common/components/ListFilters";
import ListHeader from "@/common/components/ListHeader";
import Table from "@/common/components/Table";
import TextField from "@/common/components/TextField";
import { useProductCategories } from "@/common/hooks/queries/useProductCategories";
import { useProducts } from "@/common/hooks/queries/useProducts";
import useDebounce from "@/common/hooks/useDebounce";
import { Mapper } from "@/common/services/mapper";
import { ProductCategoryData } from "@/features/productCategories/types/productCategories";
import { ProductData, ProductTableItem } from "@/features/products/types/product";
import { FileBox, Search } from "lucide-react";
import { defaultColumns } from "../../constants/columns.constant";
import { ProductsTransformer } from "@/features/products/services/productsTransformer";
import { useFilterParams } from "@/common/hooks/useFilterParams";
import { ErrorMessageWrapper } from "@/common/components/ErrorMessageWrapper";

const Products = () => {
  const { filterParams, setFilterParams } = useFilterParams();

  const [search, setSearch] = useDebounce(filterParams.search || '');

  const [searchProductCategory, setSearchProductCategory] = useDebounce('');

  const {
    data: products,
    meta,
    isPending: isPendingProducts,
    isLoading: isLoadingProducts,
    error: productsError = {} as any,
    refetch: refetchProducts,
  } = useProducts({
    filters: {
      search: search,
      page: filterParams.page || 1,
      category: filterParams.category?.value,
    },
    mapper: Mapper.mapWithTransformer<ProductData[], ProductTableItem[]>({
      transformer: ProductsTransformer.tableTransformer('Admin::Products::Show')
    }),
  });

  const { data: productCategoryOptions } = useProductCategories({
    filters: { search: searchProductCategory },
    mapper: Mapper.mapToOptions<ProductCategoryData>({ labelFieldName: 'display_name', valueFieldName: 'id' }),
  });

  return (
    <div>
      <div className="flex flex-col gap-6">
        <ListHeader
          Icon={<FileBox className="w-10 h-10" strokeWidth={1.5} />}
          title="Produtos"
          createButtonText="Novo produto"
          operationKlassToCheckPermission="Admin::Products::Create"
          dataTestid="access_products_create_button"
          disableCreateButton={isPendingProducts || productsError?.status === 403}
        />

        <ErrorMessageWrapper
          className="h-[calc(100vh-128px)]"
          statusCode={productsError?.status}
          refetchFunction={refetchProducts}
        >
          <div className="h-[calc(100vh-128px)] flex flex-col gap-6">
            <div className="flex flex-col gap-4">
              <TextField
                LeftIcon={<Search />}
                placeholder="Pesquisar"
                className="rounded-sm shadow-[4px_0px_16px_0px_rgba(0,_0,_0,_0.5)]"
                onChange={(e) => {
                  setSearch((e.target as HTMLInputElement).value);
                  setFilterParams((prev) => ({ ...prev, search: (e.target as HTMLInputElement).value }))
                }}
                value={filterParams.search}
              />

              <ListFilters
                filters={[
                  {
                    className: 'w-[160px]',
                    onInputChange: (inputValue) => setSearchProductCategory(inputValue),
                    onChange: (e) => setFilterParams((prev) => ({ ...prev, category: e })),
                    value: filterParams.category,
                    options: [{ label: 'Todas', value: '' }, ...(productCategoryOptions || [])],
                    placeholder: 'Categorias'
                  },
                ]}
              />
            </div>

            <Table
              columns={defaultColumns}
              isLoadingData={isLoadingProducts}
              page={filterParams.page || 1}
              rows={products}
              totalPages={meta.total_pages}
              totalCount={meta.total}
              hasPagination
              setPage={(page) => setFilterParams((prev) => ({ ...prev, page }))}
              rowEmptyValue="--"
            />
          </div>
        </ErrorMessageWrapper>
      </div>
    </div>
  );
};

export default Products;
